import {
  AfterViewChecked,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { ResponsiveService } from 'app/services/responsive.service';
import { getCardWidthInfo } from '../../helpers/horizontal-scroll.helpers';
import { timeDelay } from '../../helpers/time.helpers';
import { CarouselItems } from '../../models/carousel-items.enum';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-horizontal-scroller',
  templateUrl: './horizontal-scroller.component.html',
  styleUrls: ['./horizontal-scroller.component.scss'],
  standalone: true,
  imports: [NgClass],
})
export class HorizontalScrollerComponent
  implements AfterViewChecked, OnChanges, OnDestroy {
  private isSmallScreen: boolean;
  private subscriptions: Subscription[];
  private scrollInterval: any;
  private isFirstCheck: boolean;
  private isDragging: boolean = false;
  private startX: number = 0;
  private scrollLeft: number = 0;
  private hasMoved: boolean = false;

  @Input() itemType: CarouselItems;
  @Input() isBackgroundLight: boolean;
  @Input() isSmall: boolean;
  @Input() overflowVisible: boolean = false;
  @Input() activeItemId: number = null;

  @ViewChild('scrollContainer') scrollContainer: ElementRef;
  @ViewChild('leftArrow') leftArrow: ElementRef;
  @ViewChild('rightArrow') rightArrow: ElementRef;

  @HostListener('window:resize')
  onResize(): void {
    this.manageScrollingArrowsVisibility();
  }

  constructor(private responsiveService: ResponsiveService) {
    this.isSmallScreen = false;
    this.subscriptions = [
      this.responsiveService.width$.subscribe((width: number) => {
        this.isSmallScreen = width <= 480;
      }),
    ];
    this.isFirstCheck = true;
    this.itemType = CarouselItems.Other;
    this.isBackgroundLight = false;
    this.isSmall = false;
  }

  onMouseDown(event: MouseEvent): void {
    this.startDragging(event.pageX);
  }

  onTouchStart(event: TouchEvent): void {
    this.startDragging(event.touches[0].pageX);
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    this.handleDragging(event.pageX);
  }

  onTouchMove(event: TouchEvent): void {
    this.handleDragging(event.touches[0].pageX);
  }

  @HostListener('document:mouseup')
  onMouseUp(event: MouseEvent): void {
    this.stopDragging(event);
  }

  onTouchEnd(event: TouchEvent): void {
    this.stopDragging(event);
  }

  onClick(event: MouseEvent): void {
    if (this.hasMoved) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.hasMoved = false;
    }
  }

  onStopScrolling(event: Event): void {
    event.preventDefault();
    clearInterval(this.scrollInterval);
  }

  private startDragging(positionX: number): void {
    this.isDragging = true;
    this.hasMoved = false;
    this.startX = positionX - this.scrollContainer.nativeElement.offsetLeft;
    this.scrollLeft = this.scrollContainer.nativeElement.scrollLeft;
    this.scrollContainer.nativeElement.style.cursor = 'grabbing';
    this.scrollContainer.nativeElement.style.userSelect = 'none';
  }

  private handleDragging(positionX: number): void {
    if (!this.isDragging) return;
    this.hasMoved = true;
    clearInterval(this.scrollInterval);
    const x: number = positionX - this.scrollContainer.nativeElement.offsetLeft;
    const walk: number = (x - this.startX) * 2;
    this.scrollContainer.nativeElement.scrollLeft = this.scrollLeft - walk;
  }

  private stopDragging(event: Event): void {
    if (this.hasMoved && event) {
      event.stopImmediatePropagation();
    }
    this.isDragging = false;
    this.scrollContainer.nativeElement.style.cursor = 'auto';
    this.scrollContainer.nativeElement.style.userSelect = 'auto';
  }

  onScroll(direction: 'left' | 'right', event?: Event): void {
    event?.preventDefault();

    if (!this.scrollContainer || !this.scrollContainer.nativeElement) {
      console.error('Scroll container is not defined.');
      return;
    }

    clearInterval(this.scrollInterval);
    this.scroll(direction);
    this.scrollInterval = setInterval(
      () => this.scroll(direction),
      this.getScrollInterval()
    );
  }

  private async scroll(direction: 'left' | 'right'): Promise<void> {
    const sizeOfStep: number = this.getScrollStep(direction);
    for (let i = 0; i <= sizeOfStep; i += 10) {
      const step: number = this.calculateStep(direction, i, sizeOfStep);
      this.scrollContainer.nativeElement.scrollLeft += step;
      await timeDelay(5);
    }
    this.manageScrollingArrowsVisibility();
  }

  private getScrollStep(direction: 'left' | 'right'): number {
    return this.hasScrollReachedRight() &&
      direction === 'left' &&
      this.itemType !== CarouselItems.Other
      ? this.getExtraStep()
      : this.scrollSize;
  }

  private calculateStep(
    direction: 'left' | 'right',
    i: number,
    sizeOfStep: number
  ): number {
    if (this.scrollSize - i < 10) {
      return direction === 'right' ? sizeOfStep - i : i - sizeOfStep;
    }
    return direction === 'right' ? 10 : -10;
  }

  private getScrollInterval(): number {
    return this.itemType === CarouselItems.Other ? 10 : 333;
  }

  private manageScrollingArrowsVisibility(): void {
    const scrollLeft: number = this.scrollContainer.nativeElement.scrollLeft;
    this.leftArrow.nativeElement.style.display =
      scrollLeft === 0 ? 'none' : 'flex';
    this.rightArrow.nativeElement.style.display = this.hasScrollReachedRight()
      ? 'none'
      : 'flex';
  }

  private hasScrollReachedRight(): boolean {
    const width: number =
      this.scrollContainer.nativeElement.getBoundingClientRect().width;
    const scrollLeft: number = this.scrollContainer.nativeElement.scrollLeft;
    const scrollWidth: number = this.scrollContainer.nativeElement.scrollWidth;
    return Math.abs(scrollWidth - (width + scrollLeft)) <= 1;
  }

  private getExtraStep(): number {
    const width: number =
      this.scrollContainer.nativeElement.getBoundingClientRect().width;
    return this.scrollSize - (width % this.scrollSize);
  }

  get cardSize(): number {
    return this.isSmallScreen
      ? getCardWidthInfo(this.itemType).mobile
      : getCardWidthInfo(this.itemType).normal;
  }

  get scrollSize(): number {
    return this.cardSize;
  }

  private attachEventListeners(): void {
    const container: HTMLElement = this.scrollContainer.nativeElement;
    container.addEventListener('mousedown', this.onMouseDown.bind(this));
    container.addEventListener('touchstart', this.onTouchStart.bind(this));
    container.addEventListener('touchmove', this.onTouchMove.bind(this));
    container.addEventListener('touchend', this.onTouchEnd.bind(this));
    container.addEventListener('click', this.onClick.bind(this), true);
  }

  private detachEventListeners(): void {
    const container: HTMLElement = this.scrollContainer.nativeElement;
    container.removeEventListener('mousedown', this.onMouseDown.bind(this));
    container.removeEventListener('touchstart', this.onTouchStart.bind(this));
    container.removeEventListener('touchmove', this.onTouchMove.bind(this));
    container.removeEventListener('touchend', this.onTouchEnd.bind(this));
    container.removeEventListener('click', this.onClick.bind(this), true);
  }

  private scrollToActiveItem(): void {
    clearInterval(this.scrollInterval);
    const activeItem: HTMLElement =
      this.scrollContainer.nativeElement.querySelector(
        `#item-${this.activeItemId}`
      );
    if (activeItem) {
      const containerRect: DOMRect =
        this.scrollContainer.nativeElement.getBoundingClientRect();
      const itemRect: DOMRect = activeItem.getBoundingClientRect();
      if (
        itemRect.left < containerRect.left ||
        itemRect.right > containerRect.right
      ) {
        const scrollAmount: number = itemRect.left - containerRect.left;
        this.scrollContainer.nativeElement.scrollTo({
          left: this.scrollContainer.nativeElement.scrollLeft + scrollAmount,
          behavior: 'smooth',
        });
        this.manageScrollingArrowsVisibility();
      }
    }
  }

  // Lifecycle Hooks
  ngAfterViewChecked(): void {
    if (!this.isFirstCheck) return;
    const contentBox: DOMRect =
      this.scrollContainer.nativeElement.children[0].children[0].getBoundingClientRect();
    if (contentBox.width > 0) {
      this.manageScrollingArrowsVisibility();
      this.isFirstCheck = false;
      this.attachEventListeners();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.activeItemId && !changes.activeItemId.firstChange) {
      this.scrollToActiveItem();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
    this.detachEventListeners();
  }
}
